import React from 'react';
import {
  TripRequestStatus, TripMatchStatus, TripRequest,
  TripMatch,
} from '@marageti/z4-sdk/lib/travel';
import { Chip, Skeleton } from '@mui/material';
import { useApiClient, TripRequestChips, useFetch } from '@marageti/z4-lib';

const {
  ChipVerified,
  ChipPostponed,
  ChipCanceled,
  ChipReferred,
  ChipSold,
} = TripRequestChips;

const LoadingStatusSkeleton = () => (
  <Skeleton
    width={60}
  >
    <Chip />
  </Skeleton>
);

const TripRequestStatusChip = ({
  tripRequest,
  size = undefined,
  isPast = false,
}: {
  tripRequest: TripRequest,
  size?: 'small' | 'medium' | 'large',
  isPast?: boolean
}) => {
  const apiClient = useApiClient();

  const shouldFetchMatches = !!(tripRequest.id && !isPast
    // to check whether trip is Pending/Accepted
    && tripRequest.status === TripRequestStatus.MatchedStatus
  );

  const { data: matches, isLoading } = useFetch<TripMatch[]>(
    apiClient.tripRequestsClient.getTripRequestTripMatches,
    [tripRequest.id],
    shouldFetchMatches,
  );

  if (isLoading) {
    return <LoadingStatusSkeleton />;
  }

  switch (tripRequest.status) {
    case TripRequestStatus.SoldStatus: {
      if (isPast) {
        return <Chip size={size} label="Completed" color="info" />;
      }
      return <ChipSold label="Booked" size={size} />;
    }

    case TripRequestStatus.ReferredStatus:
      return <ChipReferred size={size} />;

    case TripRequestStatus.MatchedStatus: {
      const hasOneActiveMatch = matches?.some((match) => [
        TripMatchStatus.MatchActive,
        TripMatchStatus.MatchPostponed,
        TripMatchStatus.MatchInactive,
      ].includes(match.status));

      if (hasOneActiveMatch) {
        return (
          <Chip
            size={size}
            color="primary"
            label="Planning"
            variant="filled"
            sx={{
              backgroundColor: 'success.dark',
            }}
          />
        );
      }
      return <ChipVerified label="Processing" size={size} />;
    }
    case TripRequestStatus.PendingStatus:
    case TripRequestStatus.VerifiedStatus:
      return <ChipVerified label="Processing" size={size} />;
    case TripRequestStatus.PostponedStatus:
      return <ChipPostponed size={size} />;
    case TripRequestStatus.CanceledStatus:
      return <ChipCanceled color="default" sx={{ backgroundColor: 'neutral.main' }} size={size} />;
    case TripRequestStatus.JunkStatus:
    default:
      return null;
  }
};

export default TripRequestStatusChip;
